import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Table, Row, Col } from "react-bootstrap";
import { FaCalendarAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { ApiUrl } from "../../../ApiUrl";
import "./StdViewAttendance.css";

const formatDateForApi = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const StaffViewAttendance = () => {
  const navigate = useNavigate();
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [startDate, setStartDate] = useState(firstDayOfMonth);
  const [endDate, setEndDate] = useState(today);
  const [summaryData, setSummaryData] = useState({
    total_present: 0,
    total_absent: 0,
    total_classes: 0,
    attendance_percentage: 0,
  });
  const [attendanceDetails, setAttendanceDetails] = useState([]);
  const [showSummary, setShowSummary] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const attendanceChartData = [
    {
      name: "Attendance",
      Present: Number(summaryData.total_present || 0),
      Absent: Number(summaryData.total_absent || 0),
    },
  ];

  const hasSummaryChartData =
    Number(summaryData.total_present || 0) > 0 || Number(summaryData.total_absent || 0) > 0;

  const fetchAttendance = async (mode = "summary") => {
    const studentId = sessionStorage.getItem("userId");
    const academicYearId = localStorage.getItem("academicSessionId") || "";

    if (!studentId) {
      setError("Student session details are missing. Please log in again.");
      setLoading(false);
      return;
    }

    if (!startDate || !endDate) {
      setError("Please select both From Date and To Date.");
      return;
    }

    if (startDate > endDate) {
      setError("From Date cannot be greater than To Date.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const queryParams = new URLSearchParams({
        from_date: formatDateForApi(startDate),
        to_date: formatDateForApi(endDate),
      });

      if (academicYearId) {
        queryParams.append("academic_year_id", academicYearId);
      }

      const response = await fetch(
        `${ApiUrl.apiurl}STUDENT_PROFILE/GetStudentAttendance/${studentId}/?${queryParams.toString()}`
      );

      const contentType = response.headers.get("content-type") || "";
      const payload = contentType.includes("application/json")
        ? await response.json()
        : null;

      if (!response.ok || !payload || payload.message !== "Success") {
        throw new Error(payload?.message || "Unable to load attendance.");
      }

      setSummaryData(
        payload.data?.summary || {
          total_present: 0,
          total_absent: 0,
          total_classes: 0,
          attendance_percentage: 0,
        }
      );
      setAttendanceDetails(Array.isArray(payload.data?.details) ? payload.data.details : []);
      setShowSummary(mode === "summary");
    } catch (fetchError) {
      console.error("Error fetching student attendance:", fetchError);
      setSummaryData({
        total_present: 0,
        total_absent: 0,
        total_classes: 0,
        attendance_percentage: 0,
      });
      setAttendanceDetails([]);
      setError(fetchError.message || "Unable to load attendance.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendance("summary");
  }, []);

  return (
    <div className="container-fluid mt-3">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <p
                style={{
                  marginBottom: "0px",
                  textAlign: "center",
                  fontSize: "20px",
                  fontWeight: "700",
                }}
              >
                ATTENDANCE SUMMARY
              </p>

              <div className="row mb-3 mt-3 mx-0">
                <div className="col-12 d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-primary me-2"
                    style={{ width: "150px" }}
                    onClick={() => fetchAttendance("summary")}
                    disabled={loading}
                  >
                    Summary
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary me-2"
                    style={{ width: "150px" }}
                    onClick={() => fetchAttendance("details")}
                    disabled={loading}
                  >
                    Details
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger me-2"
                    style={{ width: "150px" }}
                    onClick={() => navigate("/student/dashboards")}
                  >
                    Close
                  </button>
                </div>
              </div>

              <div className="row mt-3 mx-2">
                <div className="col-12 custom-section-box mt-3 mb-4">
                  <Row className="mb-4 mt-3">
                    <Col md={5}>
                      <label className="form-label">
                        From Date <FaCalendarAlt className="ms-1" />
                      </label>
                      <DatePicker
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        dateFormat="dd/MM/yyyy"
                        className="form-control"
                        maxDate={endDate || undefined}
                      />
                    </Col>
                    <Col md={5}>
                      <label className="form-label">
                        To Date <FaCalendarAlt className="ms-1" />
                      </label>
                      <DatePicker
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        dateFormat="dd/MM/yyyy"
                        className="form-control"
                        minDate={startDate || undefined}
                        maxDate={today}
                      />
                    </Col>
                  </Row>

                  {error ? <div className="alert alert-danger">{error}</div> : null}

                  {loading ? (
                    <div className="text-center py-4">Loading attendance...</div>
                  ) : showSummary ? (
                    <>
                      <Table
                        bordered
                        responsive
                        className="text-center mb-4 student-attendance-summary-table"
                      >
                        <thead>
                          <tr className="student-attendance-summary-head">
                            <th>Total Present</th>
                            <th>Total Absent</th>
                            <th>Total Classes</th>
                            <th>Attendance %</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{summaryData.total_present}</td>
                            <td>{summaryData.total_absent}</td>
                            <td>{summaryData.total_classes}</td>
                            <td>{summaryData.attendance_percentage}%</td>
                          </tr>
                        </tbody>
                      </Table>

                      <div className="student-attendance-chart-wrap">
                        <div className="student-attendance-chart-box">
                          {hasSummaryChartData ? (
                            <ResponsiveContainer width="100%" height={260}>
                              <BarChart
                                data={attendanceChartData}
                                margin={{ top: 18, right: 20, left: 0, bottom: 10 }}
                              >
                                <CartesianGrid stroke="#e5e7eb" vertical={false} />
                                <XAxis
                                  dataKey="name"
                                  tick={false}
                                  axisLine={{ stroke: "#d1d5db" }}
                                  tickLine={false}
                                />
                                <YAxis
                                  allowDecimals={false}
                                  axisLine={{ stroke: "#d1d5db" }}
                                  tickLine={false}
                                />
                                <Legend verticalAlign="bottom" height={36} />
                                <Bar dataKey="Present" fill="#22c55e" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="Absent" fill="#ef4444" radius={[4, 4, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="student-attendance-no-chart-data">
                              <div className="student-attendance-empty-axis">
                                <span className="student-attendance-empty-y-top">1</span>
                                <span className="student-attendance-empty-y-bottom">0</span>
                                <span className="student-attendance-empty-label">No Data</span>
                              </div>
                              <div className="student-attendance-empty-legend">
                                <span className="student-attendance-legend-item">
                                  <span className="student-attendance-legend-swatch student-attendance-present" />
                                  Present
                                </span>
                                <span className="student-attendance-legend-item">
                                  <span className="student-attendance-legend-swatch student-attendance-absent" />
                                  Absent
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <Table
                      striped
                      bordered
                      responsive
                      className="text-center mt-4"
                      style={{
                        maxWidth: "1000px",
                        margin: "auto",
                        border: "1px solid #ccc",
                      }}
                    >
                      <thead>
                        <tr style={{ backgroundColor: "#d89443" }}>
                          <th>Sr. No</th>
                          <th>Date</th>
                          <th>Subject</th>
                          <th>Attendance</th>
                          <th>Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {attendanceDetails.length > 0 ? (
                          attendanceDetails.map((record) => (
                            <tr key={`${record.date}-${record.sr_no}`}>
                              <td>{record.sr_no}</td>
                              <td>{record.date || "-"}</td>
                              <td>{record.subject || "-"}</td>
                              <td>{record.attendance || "-"}</td>
                              <td>{record.remarks || "-"}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center">
                              No attendance records available for the selected date range
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffViewAttendance;
